import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import React, { useEffect, useState, useCallback } from "react";
import DOMPurify from 'dompurify';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { fetchPublicCampaign } from "../../services/api/campaign";
import CampaignImageSlider from "../../components/campaign/CampaignImageSlider";
import StrategyIcon from "../../components/icons/StrategyIcon";
import Loader from "../../components/loaders/Loader";
import usePageTitle from "../../hooks/usePageTitle";
import { RootState } from "../../redux-stores/store";
import { CampaignData } from "../../types/sponsorships/campaigns";
import { arrayOfUniqueStrings, textToTitleCase, sanitizeURLs } from "../../utils/helpers";
import { getLocalStorageItem, setLocalStorageItem } from "../../utils/localStorage";
import { platforms } from "../../constants/constants";
import instagramLogo from "../../assets/images/platforms/platform-instagram.png"
import youtubeLogo from "../../assets/images/platforms/platform-youtube.png"

const platformLogos = {
    ig: instagramLogo,
    yt: youtubeLogo
}
const CampaignPublic: React.FC = () => {
    /* Page parameters */
    const { campaignId } = useParams();
    const [searchParams] = useSearchParams();
    const referrer = searchParams.get('referrer');
    const [campaignPlatform, setCampaignPlatform] = useState<{id: string, name: string} | null>(null);

    if (referrer && campaignId) {
        const campaignReferrals = getLocalStorageItem('campaign_referrals');
        setLocalStorageItem('campaign_referrals', { ...campaignReferrals, [campaignId]: referrer });
    }

    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    /* Page parameters */

    /* Local States */
    const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
    /* Local States */

    /* Hooks */
    const navigate = useNavigate();
    // const confirmationBox = useConfirmation();
    usePageTitle(campaignData?.campaign?.name ?? '')
    /* Hooks */


    /* Data handling functions */
    const fetchCampaignData = useCallback(async () => {
        if (!campaignId) return;
        const campaign_data = await fetchPublicCampaign(campaignId);
        setCampaignData(campaign_data);
    }, [campaignId])

    /* Data handling functions */

    /* Helper functions */
    const getCampaignImages = () => {
        let images: string[] = campaignData?.campaign.hero_img_url ? [campaignData.campaign.hero_img_url] : [];
        if (campaignData?.campaign.gallery_images)
            images = [...images, ...campaignData.campaign.gallery_images];

        return arrayOfUniqueStrings(images);
    }

    const shareURL = async () => {
        if (campaignData) {
            try {
                let share_url = campaignData?.share_url;
                const referralCode = (new URLSearchParams(window.location.search)).get('referrer');
                if (referralCode) {
                    share_url += '/?referrer=' + referralCode
                }
                await navigator.clipboard.writeText(share_url);
                toast.success('URL copied to your clipboard! 🙂');
            } catch (error) {
                console.error('Failed to copy text');
            }
        }
    }
    /* Helper functions */

    /* Lifecycle hooks */
    useEffect(() => {
        fetchCampaignData();
    }, [campaignId, fetchCampaignData])

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/campaign/' + campaignId);
        }
    }, [isAuthenticated, campaignId, navigate])

    useEffect(() => {
            if(campaignData){
                const platformKey = campaignData.campaign.platform;
                const platform = platforms[platformKey as keyof typeof platforms];
                setCampaignPlatform(platform ?? null);
            }
        }, [campaignData])

    /* Lifecycle hooks */


    /* Renders */
    if (!campaignData) return <Loader isLoading={true} />

    return (
        <>
            <div className="card card-style p-3 m-0 mb-5">
                <div className="row">
                    <div className="col-xl-12">
                        <div>
                            <CampaignImageSlider images={getCampaignImages()} />
                        </div>
                        <div className="d-flex flex-row justify-content-between mb-2">
                            {/* <span className="chip mb-0 bg-gray-light">
                            <StrategyIcon strategy={campaignData.campaign.strategy} />
                            <strong className="color-black font-400">{textToTitleCase(campaignData.campaign.strategy)}</strong>
                        </span> */}
                            <div>
                                <img src={platformLogos[campaignPlatform?.id as keyof typeof platformLogos]} height={campaignPlatform?.id === 'ig' ? 35 : 25} />
                            </div>
                            <div>
                                <a href={'https://wa.me/?text=' + encodeURIComponent(campaignData.share_url)} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-whatsapp"><i className="fab fa-whatsapp"></i></a>
                                {/* <a href={'https://www.linkedin.com/shareArticle?mini=true&url=' + encodeURIComponent(campaignData.share_url) + '&title=' + campaignData.campaign.name} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-linkedin"><i className="fa-brands fa-linkedin"></i></a> */}
                                <a href={'https://x.com/intent/tweet?url=' + encodeURIComponent(campaignData.share_url) + '&text=' + campaignData.campaign.name} target="_blank" rel="noreferrer" className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-dark text-white"><i className="fa-brands fa-x-twitter"></i></a>
                                <button className="icon ms-2 icon-xxs rounded-sm shadow-l mr-1 bg-linkedin" onClick={shareURL}><i className="fas fa-share-nodes"></i></button>
                            </div>
                        </div>
                        <h4 className="mt-2" style={{ fontWeight: 500 }}>{campaignData.campaign.name}</h4>
                        <div className="mb-4">
                            {
                                campaignData.campaign.niche_names?.map((niche, index) => {
                                    return (
                                        <span key={index} className="chip mb-0 mt-2 me-2 ps-2 pe-2 bg-gray-light">
                                            <strong className="color-black font-400">{niche}</strong>
                                        </span>
                                    );
                                })
                            }
                        </div>
                    </div>
                    <div className="col-xl-12">
                        <div className="card shadow-none border-gray-300" style={{ borderRadius: '1rem' }}>
                            <div className="card-body p-2">
                                <h3 className="pop fw-bold">Specifications</h3>
                                <ul className="list-group list-group-flush">
                                    {
                                        campaignData.brand &&
                                        <li className="list-group-item px-0 d-flex justify-content-between" v-if="brand">
                                            <span className="left">Brand</span>
                                            <span className="right">{campaignData.brand.name}</span>
                                        </li>
                                    }
                                    {/* <li className="list-group-item px-0 d-flex justify-content-between">
                                    <span className="left">Strategy</span>
                                    <span className="right">{campaignData.campaign.strategy_name}</span>
                                </li> */}
                                    {
                                        (Number(campaignData?.campaign?.budget) > 0) &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Payment</span>
                                            <span className="right text-end">
                                                upto ₹{Number(campaignData.campaign.budget)}
                                            </span>
                                        </li>
                                    }
                                    {
                                        (Number(campaignData?.campaign?.barter_budget) > 0) &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Barter/Reimbursement</span>
                                            <span className="right text-end">
                                                upto ₹{campaignData.campaign.barter_budget}
                                            </span>
                                        </li>
                                    }
                                    {
                                        campaignData.campaign?.requirements?.gender !== 'A' &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Influencer's Gender</span>
                                            <span className="right">{campaignData.campaign.requirements?.gender_name}</span>
                                        </li>
                                    }
                                    {
                                        (campaignData.campaign?.requirements?.age?.min ? true : false) &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Influencer's Age</span>
                                            <span className="right">{campaignData.campaign?.requirements?.age?.min} - {campaignData.campaign?.requirements?.age?.max}</span>
                                        </li>
                                    }
                                    {
                                        campaignData.campaign?.requirements?.regions && campaignData.campaign?.requirements?.regions.length > 0 &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Influencer Region(s)</span>
                                            <span className="right text-end">
                                                {
                                                    campaignData.campaign?.requirements?.regions.map((region, i) => {
                                                        return (
                                                            <div className="badge badge-light mb-1 me-1 d-block text-start" key={i}>{region}</div>
                                                        )
                                                    })
                                                }
                                            </span>
                                        </li>
                                    }
                                    {
                                        campaignData.campaign?.requirements?.cities && campaignData.campaign?.requirements?.cities.length > 0 &&
                                        (!Array.isArray(campaignData?.campaign?.requirements?.regions) || campaignData?.campaign?.requirements?.regions.length === 0) &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Influencer Location(s)</span>
                                            <span className="right text-end">
                                                {
                                                    campaignData.campaign?.requirements?.cities.map((city, i) => {
                                                        return (
                                                            <div className="badge badge-light mb-1 me-1 d-block text-start" key={i}>{city}</div>
                                                        )
                                                    })
                                                }
                                            </span>
                                        </li>
                                    }
                                    {
                                        (campaignData.campaign?.requirements?.follower_count?.min ? true : false) &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Followers</span>
                                            <span className="right">{campaignData.campaign?.requirements?.follower_count?.min}+</span>
                                        </li>
                                    }
                                    {
                                        campaignData.campaign?.requirements?.audience_gender !== 'A' &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Audience Gender</span>
                                            <span className="right">{campaignData.campaign.requirements?.audience_gender_name}</span>
                                        </li>
                                    }
                                    {
                                        campaignData.campaign?.requirements?.audience_cities && campaignData.campaign?.requirements?.audience_cities.length > 0 &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Audience Location(s)</span>
                                            <span className="right text-end">
                                                {
                                                    campaignData.campaign?.requirements?.audience_cities.map((city, i) => {
                                                        return (
                                                            <div className="badge badge-light mb-1 me-1 d-block text-start" key={i}>{city}</div>
                                                        )
                                                    })
                                                }
                                            </span>
                                        </li>
                                    }
                                    {
                                        campaignData.campaign?.requirements?.audience_age_groups && campaignData.campaign?.requirements?.audience_age_groups.length > 0 &&
                                        <li className="list-group-item px-0 d-flex justify-content-between">
                                            <span className="left">Audience Age</span>
                                            <span className="right text-end">
                                                {
                                                    campaignData.campaign?.requirements?.audience_age_groups.map((age, i) => {
                                                        return (
                                                            <div className="badge badge-light mb-1 me-1" key={i}>{age}</div>
                                                        )
                                                    })
                                                }
                                            </span>
                                        </li>
                                    }
                                </ul>
                            </div>
                        </div>

                    </div>
                </div>
                <div className="col-12">
                    {
                        campaignData?.campaign?.description &&
                        <div>
                            <h4 className="pop">Description</h4>
                            <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sanitizeURLs(campaignData?.campaign?.description ?? "")) }}></div>
                        </div>
                    }
                    {
                        Array.isArray(campaignData?.milestones) && campaignData.milestones.length > 0 &&
                        <div className="specification-card p-0 my-4">
                            <div className="specification-card-header">
                                <h6 className="specification-card-title pop">Media Requirements</h6>
                                {/* <img src={MilestoneMediaPNG} className='milestone-spec-illustrator' alt='milestone-payout' /> */}
                            </div>
                            {
                                [...campaignData.milestones].sort((a, b) => a.sort_order - b.sort_order).map((milestone, i) => (
                                    <div className='specification-card-content' key={i}>
                                        {
                                            milestone.gig_type.slug !== 'general' &&
                                            <h6 className="m-0 mt-2" style={{ fontWeight: 500 }}>{(campaignData.milestones ?? []).length > 1 ? `#${milestone.sort_order} ` : ''}{milestone.gig_type.name}</h6>
                                        }
                                        <div className="milestone-media-requirements">
                                            {
                                                campaignData?.campaign.platform === 'yt' &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.content_media_types.VIDEO}</span>
                                                    <span>Video</span>
                                                </div>
                                            }
                                            {
                                                campaignData?.campaign.platform === 'ig' &&
                                                <>
                                                    <div className="milestone-media-requirement">
                                                        <span>{milestone.content_media_types.REELS}</span>
                                                        <span>Reel</span>
                                                    </div>
                                                    <div className="milestone-media-requirement">
                                                        <span>{milestone.content_media_types.STORY}</span>
                                                        <span>Story</span>
                                                    </div>
                                                    <div className="milestone-media-requirement">
                                                        <span>{milestone.content_media_types.FEED}</span>
                                                        <span>Post</span>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                        <div className="milestone-media-requirements">
                                            {
                                                milestone.payment_days &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.payment_days} <span className="subtext">day{milestone.payment_days > 1 ? 's' : ''}</span></span>
                                                    <span>Payment</span>
                                                </div>
                                            }
                                            {
                                                milestone?.specifications?.content_rights_duration &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.specifications.content_rights_duration} <span className='subtext'>month{Number(milestone.specifications.content_rights_duration) > 1 ? 's' : ''}</span></span>
                                                    <span>Content Rights</span>
                                                </div>
                                            }
                                            {
                                                milestone?.specifications?.ad_rights_duration &&
                                                <div className="milestone-media-requirement">
                                                    <span>{milestone.specifications.ad_rights_duration} <span className='subtext'>month{Number(milestone.specifications.ad_rights_duration) > 1 ? 's' : ''}</span></span>
                                                    <span>Ad Rights</span>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                ))
                            }

                        </div>
                    }
                    {
                        campaignData.brand && campaignData.brand.description &&
                        <div className="mt-2">
                            <h4 className="pop">About the Brand</h4>
                            {
                                campaignData.brand.website_url &&
                                <a href={campaignData.brand.website_url} target="_blank" rel="noopener noreferrer">{campaignData.brand.website_url}</a>
                            }
                            <p style={{ whiteSpace: 'pre-wrap' }}>{campaignData.brand.description}</p>
                        </div>
                    }
                </div>
            </div>
            <div className="sticky-footer" style={{ marginLeft: '-15px' }}>
                <Link to={"/campaign/" + campaignData.campaign.uuid} className="w-100 create-earning-link-btn">Join Us to Apply</Link>
            </div>
        </>

    );
}

export default CampaignPublic;